import { supabase } from '@/lib/supabase';

export interface AppSetting {
  id: string;
  setting_key: string;
  setting_value: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface LogoHistory {
  id: string;
  logo_url: string;
  uploaded_by: string;
  created_at: string;
  is_active: boolean;
}

export class AppSettingsService {
  static async getSetting(key: string): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('setting_value')
        .eq('setting_key', key)
        .single();

      if (error) {
        console.error('Error getting app setting:', error);
        return null;
      }

      return data?.setting_value || null;
    } catch (error) {
      console.error('Error in getSetting:', error);
      return null;
    }
  }

  static async updateLogoUrl(logoUrl: string): Promise<boolean> {
    try {
      const { error } = await supabase.rpc('update_logo_setting', {
        new_logo_url: logoUrl
      });

      if (error) {
        console.error('Error updating logo URL:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateLogoUrl:', error);
      return false;
    }
  }

  static async updateAiPropDefaultLink(defaultLink: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('app_settings')
        .update({ 
          setting_value: defaultLink, 
          updated_at: new Date().toISOString() 
        })
        .eq('setting_key', 'ai_prop_default_link');

      if (error) {
        console.error('Error updating AI Prop default link:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateAiPropDefaultLink:', error);
      return false;
    }
  }

  static async getLogoHistory(limit: number = 10): Promise<LogoHistory[]> {
    try {
      const { data, error } = await supabase
        .from('logo_history')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error getting logo history:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getLogoHistory:', error);
      return [];
    }
  }

  static async getCurrentLogoUrl(): Promise<string> {
    const logoUrl = await this.getSetting('logo_url');
    return logoUrl || 'https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl/icon%20logo%20trade%20bot%20365%20white.png';
  }

  static async getAiPropDefaultLink(): Promise<string> {
    const defaultLink = await this.getSetting('ai_prop_default_link');
    return defaultLink || 'https://aiprop.com/default-ref';
  }

  static async getJavaServiceUrl(): Promise<string> {
    const serviceUrl = await this.getSetting('java_service_url');
    return serviceUrl || 'https://your-java-service.com/api/crawl';
  }

  static async updateJavaServiceUrl(serviceUrl: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('app_settings')
        .update({ 
          setting_value: serviceUrl, 
          updated_at: new Date().toISOString() 
        })
        .eq('setting_key', 'java_service_url');

      if (error) {
        console.error('Error updating Java service URL:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateJavaServiceUrl:', error);
      return false;
    }
  }

  static async getSupabaseUrl(): Promise<string> {
    const supabaseUrl = await this.getSetting('supabase_url');
    return supabaseUrl || 'https://your-supabase-url.supabase.co';
  }

  static async updateSupabaseUrl(supabaseUrl: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('app_settings')
        .update({ 
          setting_value: supabaseUrl, 
          updated_at: new Date().toISOString() 
        })
        .eq('setting_key', 'supabase_url');

      if (error) {
        console.error('Error updating Supabase URL:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateSupabaseUrl:', error);
      return false;
    }
  }

  // REMOVED: getSupabaseKey and getSupabaseServiceKey for security
  // These secrets are now handled server-side via Edge Functions

  // Validate configuration via Edge Function (secure server-side validation)
  static async validateConfiguration(config?: any): Promise<{success: boolean, issues?: string[]}> {
    try {
      const { data, error } = await supabase.functions.invoke('admin-config', {
        body: { action: 'validate', config }
      });
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Configuration validation failed:', error);
      return { success: false, issues: ['Validation service unavailable'] };
    }
  }

  // Test database connection via Edge Function
  static async testDatabaseConnection(config?: any): Promise<{success: boolean, issues?: string[]}> {
    try {
      const { data, error } = await supabase.functions.invoke('admin-config', {
        body: { action: 'test_connection', config }
      });
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Database connection test failed:', error);
      return { success: false, issues: ['Connection test service unavailable'] };
    }
  }

  // Market Analysis Settings Methods
  static async getMarketAnalysisAffiliateOnlyMode(): Promise<boolean> {
    const setting = await this.getSetting('market_analysis_affiliate_only');
    return setting === 'true';
  }

  static async getMarketAnalysisAdminOverride(): Promise<boolean> {
    const setting = await this.getSetting('market_analysis_admin_override');
    return setting === 'true';
  }

  static async updateMarketAnalysisAffiliateOnly(enabled: boolean): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('app_settings')
        .upsert({ 
          setting_key: 'market_analysis_affiliate_only',
          setting_value: enabled.toString(),
          updated_at: new Date().toISOString()
        }, { 
          onConflict: 'setting_key' 
        });

      if (error) {
        console.error('Error updating market analysis affiliate only setting:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateMarketAnalysisAffiliateOnly:', error);
      return false;
    }
  }

  static async updateMarketAnalysisAdminOverride(enabled: boolean): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('app_settings')
        .upsert({ 
          setting_key: 'market_analysis_admin_override',
          setting_value: enabled.toString(),
          updated_at: new Date().toISOString()
        }, { 
          onConflict: 'setting_key' 
        });

      if (error) {
        console.error('Error updating market analysis admin override setting:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateMarketAnalysisAdminOverride:', error);
      return false;
    }
  }

  // Scheduler Settings Methods
  static async getSchedulerEndpointUrl(): Promise<string> {
    const endpointUrl = await this.getSetting('scheduler_endpoint_url');
    return endpointUrl || 'https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/scheduler';
  }

  static async updateSchedulerEndpointUrl(endpointUrl: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('app_settings')
        .upsert({ 
          setting_key: 'scheduler_endpoint_url',
          setting_value: endpointUrl,
          updated_at: new Date().toISOString()
        }, { 
          onConflict: 'setting_key' 
        });

      if (error) {
        console.error('Error updating scheduler endpoint URL:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateSchedulerEndpointUrl:', error);
      return false;
    }
  }

  // Email Template Logo Methods
  static async getEmailTemplateLogo(): Promise<string> {
    const logoUrl = await this.getSetting('email_template_logo_url');
    return logoUrl || 'https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl/icon%20logo%20trade%20bot%20365%20white.png';
  }

  static async updateEmailTemplateLogo(logoUrl: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('app_settings')
        .upsert({ 
          setting_key: 'email_template_logo_url',
          setting_value: logoUrl,
          updated_at: new Date().toISOString()
        }, { 
          onConflict: 'setting_key' 
        });

      if (error) {
        console.error('Error updating email template logo URL:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateEmailTemplateLogo:', error);
      return false;
    }
  }
}
